<template>
  <div
    class="PlanFinder"
    :class="{
      'is-disabled': disabled,
      'is-expanded': isExpanded,
    }"
  >
    <!-- Toggle Button -->
    <ActionButton
      variant="primary"
      size="md"
      :disabled="disabled"
      :rightIcon="buttonIcon"
      class="PlanFinder-button"
      :class="{
        'is-disabled': disabled,
        'is-expanded': isExpanded,
      }"
      @click="handleToggle"
    >
      {{ buttonText }}
    </ActionButton>

    <div
      v-if="isExpanded"
      class="PlanFinder-panel"
    >
      <div class="PlanFinder-header">
        <p v-if="description" class="PlanFinder-description">
          {{ description }}
        </p>
      </div>

      <div class="PlanFinder-fields">
        <Autocomplete
          v-model="outboundFieldValue"
          class="PlanFinder-field"
          :options="outboundFieldOptions"
          :placeholder="outboundFieldPlaceholder"
          :disabled="disabled"
          :label="outboundFieldLabel"
          clearable
          @update:model-value="handleOutboundFieldChange"
        />

        <Autocomplete
          v-model="inboundFieldValue"
          class="PlanFinder-field"
          :options="inboundFieldOptions"
          :placeholder="inboundFieldPlaceholder"
          :disabled="disabled || !hasOutboundValue"
          :label="inboundFieldLabel"
          clearable
          @update:model-value="handleInboundFieldChange"
        />
      </div>
    </div>

    <div
      class="PlanFinder-resultsText"
      :class="{ 'is-hidden': !hasValues }"
    >
      {{ resultsText }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import { useStation } from '@cvo/ui/composables';
import Autocomplete from '../Autocomplete/Autocomplete.vue';
import ActionButton from '../../atoms/ActionButton/ActionButton.vue';
import type { PlanFinderProps, PlanFinderEmits } from './PlanFinder.model';

const props = withDefaults(defineProps<PlanFinderProps>(), {
  buttonText: 'Find Plans',
  description: 'Search description',
  outboundFieldLabel: 'From',
  inboundFieldLabel: 'To',
  outboundFieldPlaceholder: 'Select departure',
  inboundFieldPlaceholder: 'Select destination',
  outboundFieldOptions: () => [],
  inboundFieldOptions: () => [],
  disabled: false,
});

const emit = defineEmits<PlanFinderEmits>();
const isExpanded = ref(false);

const { t } = useI18n();

// Models
const outboundFieldValue = defineModel<string | undefined>('outboundFieldValue', { default: undefined });
const inboundFieldValue = defineModel<string | undefined>('inboundFieldValue', { default: undefined });

// Computed
const hasOutboundValue = computed(() => outboundFieldValue.value !== undefined);

const hasValues = computed(() => {
  return Boolean(outboundFieldValue.value) || Boolean(inboundFieldValue.value);
});

const buttonIcon = computed(() => {
  return isExpanded.value ? 'chevron-up' : 'chevron-down';
});

const { stationName: originName } = useStation(outboundFieldValue);
const { stationName: destinationName } = useStation(inboundFieldValue);

const resultsText = computed(() => {
  if(!hasValues.value) return '';

  const destination = inboundFieldValue.value
    ? destinationName.value
    : t('ui.components.planFinder.destinationPlaceholder');

  return t('ui.components.planFinder.results', { origin: originName.value, destination });
});

// Methods
function handleToggle() {
  if (props.disabled) return;
  isExpanded.value = !isExpanded.value;

  if (isExpanded.value) {
    emit('open');
  } else {
    emit('close');
  }
}

function handleOutboundFieldChange(value: string | undefined) {
  if(!Boolean(value)) {
    inboundFieldValue.value = undefined;
  }

  emit('outboundFieldSelect', value);
}

function handleInboundFieldChange(value: string | undefined) {
  emit('inboundFieldSelect', value);
}
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/responsive';

@layer cvo-ui {
  .PlanFinder {
    position: relative;
    width: 100%;
    color: vars.$organisms-planfinder-text-color;

    &.is-disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    &-button {
      transition: border-radius 0.2s ease;
      --button-radius: #{vars.$organisms-planfinder-lg-md-border-radius};
      --button-color: #{vars.$organisms-planfinder-bg-color};
      color: vars.$organisms-planfinder-title-color;

      @include responsive.mobile {
        --button-radius: #{vars.$organisms-planfinder-sm-border-radius};
        width: 100%;
        justify-content: flex-start;
      }

      &.is-expanded {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }

      .Icon {
        color: vars.$organisms-planfinder-icon-color;
      }
    }

    &-panel {
      border-radius: 0 vars.$organisms-planfinder-lg-md-border-radius vars.$organisms-planfinder-lg-md-border-radius vars.$organisms-planfinder-lg-md-border-radius;
      background-color: vars.$organisms-planfinder-bg-color;
      padding: vars.$dimensions-d-4;
      box-shadow: vars.$shadow-lg;
      animation: slide-down 0.2s ease-out;

      @include responsive.mobile {
        border-radius: 0 0 vars.$organisms-planfinder-sm-border-radius vars.$organisms-planfinder-sm-border-radius;
      }
    }

    &-resultsText {
      margin-top: vars.$dimensions-d-4;
      color: vars.$organisms-planfinder-text-color;

      &.is-hidden {
        visibility: hidden;

        &::before {
          content: "\00a0";
        }
      }
    }

    &-header {
      margin-bottom: vars.$dimensions-d-4;
    }

    &-description {
      color: vars.$organisms-planfinder-subtitle-color;
    }

    &-fields {
      display: flex;
      gap: vars.$dimensions-d-4;
    }

    &-field {
      flex: 1;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .PlanFinder-container {
      transition: none;
      animation: none;
    }
  }

  @media (prefers-contrast: high) {
    .PlanFinder-container {
      border-width: 2px;
    }
  }
}
</style>
